/**
 * questionnaireProgress.ts — how far through the clinical questions a patient
 * actually got, and which one they stopped on.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY IT EXISTS. The Patient journey panel's Questionnaire rung has to say
 * "All 14 questions answered", or "7 of 14 — stopped at Q8, other medications",
 * or — where the read is redacted and completeness genuinely cannot be
 * established — "2 of 14 answered — some questions are not visible to you"
 * (CTO 2026-09-07). Nothing on the deal aggregate can answer that: the card
 * carries `assessmentState`, the detail carries `submittedAt`, and neither knows
 * a count. The staff review projection is no help either — clinic-service builds
 * its sections from ANSWERED questions only ("only ANSWERED questions appear;
 * empty sections are dropped"), so it can state the numerator and never the
 * denominator.
 *
 * SO IT IS TWO READS, BOTH ALREADY IN USE BY THIS DRAWER:
 *   GET /v1/clinic/assessments/:id/questions  — the ordered bank (SinglePageForm)
 *   GET /v1/clinic/assessments/:id/answers    — the recorded answers (QuestionnairePanel)
 * They run in parallel and neither blocks the panel: the rung renders without a
 * progress line until they land, and keeps rendering without one if they fail.
 * A missing count is a missing detail; a wrong count on a clinical record is a
 * defect.
 *
 * ⚠️ R19 — THE ANSWER VALUES NEVER ENTER THIS MODULE'S STATE. `getAssessmentAnswers`
 * returns the patient's real clinical answers; the only thing this needs is
 * WHICH questions have one, so the hook takes `Object.keys()` at the boundary
 * and drops the values on the floor in the same expression. Nothing downstream
 * can render an answer it was never given, which is a structural guarantee
 * rather than a rule somebody has to remember. Prompts come from the question
 * bank — the same words the patient was shown, and not clinical content.
 */
import { useEffect, useState } from 'react';
import { getAssessmentAnswers } from '../api/deals';
import { getQuestions } from '../api/journey';
import type { QuestionPublic } from '../api/types';

/** Where a patient stopped: the question's own `idx`, and the words they saw. */
export interface QuestionnaireStop {
  /** The question's own `idx` — see the WHY on `stoppedAt` in the derivation. */
  number: number;
  prompt: string;
}

/**
 * ⚠️ DID THEY FINISH? THE THIRD ANSWER IS "WE CANNOT TELL", AND IT IS THE WHOLE
 * REASON THIS FIELD EXISTS.
 *
 * `stoppedAt === null` was being read downstream as "finished", and it does not
 * mean that. It means every question we were SERVED has an answer — and the
 * served list is R28-REDACTED while the denominator is the BANK, so on a
 * redacted read the two differ by the number of withheld questions. A patient
 * who had answered everything they had been SHOWN therefore read as one who had
 * answered everything there is: a green tick and the words "All 14 questions
 * answered" over a 14% bar, beside an action band that said they had not
 * finished. On a clinical surface that is worse than showing nothing — a
 * clinician could approve on the strength of a completeness claim that is false.
 *
 *  complete    every clinical question in the bank has an answer AND we were
 *              served the whole bank, so the count is the whole story.
 *  incomplete  a question we WERE served has no answer. `stoppedAt` names it.
 *  unknowable  everything we were served is answered, but the server withheld
 *              part of the bank — so whether the patient has finished is not
 *              something this read can know, and the panel must say so rather
 *              than assert either way.
 */
export type QuestionnaireCompleteness = 'complete' | 'incomplete' | 'unknowable';

/** The counts every verdict carries. */
interface QuestionnaireCounts {
  /** Clinical questions with a recorded answer. */
  answered: number;
  /** Clinical questions in the bank — see the derivation for why this is not `questions.length`. */
  total: number;
}

/**
 * The rung's progress line, already counted AND already judged.
 *
 * ⚠️ A UNION RATHER THAN A FLAT SHAPE, so `stoppedAt` exists exactly where it
 * means something. A caller cannot reach for it on a finished form, and cannot
 * infer completeness from its absence — which is precisely the inference that
 * put the tick over the 14% bar. The verdict is a FIELD that has to be read, not
 * a null that can be misread.
 */
export type QuestionnaireProgress =
  | (QuestionnaireCounts & { completeness: 'complete'; stoppedAt: null })
  | (QuestionnaireCounts & { completeness: 'unknowable'; stoppedAt: null })
  | (QuestionnaireCounts & { completeness: 'incomplete'; stoppedAt: QuestionnaireStop });

/**
 * THE TWO KINDS THAT ARE NOT CLINICAL QUESTIONS.
 *
 * `photo` and `consent` are steps of the form, not questions about the patient's
 * health, and each has its OWN rung on the journey rail — counting them here
 * would both inflate the denominator and say the same thing twice.
 *
 * ⚠️ DERIVED FROM `kind`, NEVER FROM THE KEYS 'q14'/'q15'. The weight step was
 * `kind: 'photo'` until 2026-09-04 and is `kind: 'consent'` now (clinic-service's
 * seed replaced the mandatory upload with a self-certification declaration —
 * requiring an image from every patient had taken throughput from ~25 leads a
 * week to ~2). Both shapes are live: journeys already in treatment ran on the
 * old set. A key-based partition would have counted 15 clinical questions on one
 * and 14 on the other, silently.
 */
const NON_CLINICAL_KINDS: readonly QuestionPublic['kind'][] = ['photo', 'consent'];

function isClinical(question: QuestionPublic): boolean {
  return !NON_CLINICAL_KINDS.includes(question.kind);
}

/**
 * Count the clinical progress from a served question bank and the keys of the
 * answers on file. PURE — the hook below owns the I/O.
 *
 * Returns `null` for an empty bank, and for one holding no clinical questions at
 * all: nothing to answer means no progress line, never "0 of 0".
 */
export function deriveQuestionnaireProgress(
  questions: readonly QuestionPublic[],
  answerKeys: readonly string[],
): QuestionnaireProgress | null {
  if (questions.length === 0) return null;

  const served = [...questions].sort((a, b) => a.idx - b.idx);
  const nonClinical = served.filter((q) => !isClinical(q));
  const clinical = served.filter(isClinical);

  /**
   * ⚠️ THE DENOMINATOR IS THE BANK, NOT THE SERVED LIST, AND THAT IS THE WHOLE
   * SUBTLETY OF THIS FUNCTION.
   *
   * `GET .../questions` is R28-REDACTED: clinic-service withholds every question
   * marked `surface: 'assessment'` until the final BMI/age gate has genuinely
   * passed (today that is the two POM questions, q10 and q11). So a patient
   * mid-flow is served 14 questions where the bank holds 16, and
   * `clinical.length` would print "5 of 12" for a form that has 14 clinical
   * questions in it — a denominator that GROWS as the patient answers, which is
   * worse than no number at all.
   *
   * `idx` is the fix: redaction REMOVES questions, it never renumbers them, so
   * the highest served `idx` still names the size of the bank. The non-clinical
   * steps sit at the END of that bank and are never redacted (they carry no POM
   * content), so subtracting the served ones is exact in both cases.
   */
  const bankSize = served[served.length - 1].idx + 1;
  const total = bankSize - nonClinical.length;

  /**
   * A bank with no clinical questions in it has no progress to report. "All 0
   * questions answered" is a sentence, and it is not a true one — so this joins
   * the empty-bank case above and returns nothing rather than a claim.
   */
  if (total <= 0) return null;

  /**
   * The numerator counts ANSWER KEYS, not served questions — a withheld question
   * is by construction unanswered (the gate only opens forwards), so keys are
   * the complete record and they survive the redaction the served list does not.
   * Non-clinical answers are excluded by key so the consent tick does not read as
   * a fifteenth clinical answer.
   *
   * Clamped to `total`: a bank that shrank between the two reads must not print
   * "15 of 14".
   */
  const nonClinicalKeys = new Set(nonClinical.map((q) => q.key));
  const answered = Math.min(answerKeys.filter((key) => !nonClinicalKeys.has(key)).length, total);

  /**
   * WHERE THEY STOPPED. The first served clinical question with no answer.
   *
   * ⚠️ THE NUMBER IS THE QUESTION'S OWN `idx`, NOT ITS POSITION IN THE SERVED
   * LIST. The bank numbers itself (`q8` is seeded at `idx: 8`), so "Q8" names
   * the same question to a staff member reading this panel, a clinician reading
   * the Questionnaire tab and an engineer reading an audit row — and it does not
   * shift when the server redacts a question above it. The PROMPT is the half a
   * desk acts on; the number is the locator.
   */
  const answeredKeys = new Set(answerKeys);
  const next = clinical.find((q) => !answeredKeys.has(q.key));
  if (next) {
    return { answered, total, completeness: 'incomplete', stoppedAt: { number: next.idx, prompt: next.prompt } };
  }

  /**
   * ⚠️ AND NOW THE JUDGEMENT THE OLD SHAPE COULD NOT MAKE. Every question we
   * were served is answered — which is NOT the same fact as "the form is
   * finished", and reading it as one is the defect this returns a verdict to
   * prevent.
   *
   * `withheld` is the bank minus the served list: under R28 that is the POM
   * questions clinic-service holds back until the final BMI/age gate passes. If
   * ANY of them are missing we have not seen the form, so "they answered
   * everything" is a claim about the part we were shown and nothing more. It is
   * reported as `unknowable` — an honest third answer — rather than dressed up
   * as completion. Only a bank we were served WHOLE can be called complete.
   *
   * It leans on the same fact `bankSize` does: the non-clinical steps sit at the
   * END of the bank and are never withheld (they carry no POM content), so the
   * highest served `idx` really is the bank's size and this subtraction really is
   * the count of what was held back. If that ever stops being true, `total` is
   * wrong before this is.
   */
  const withheld = bankSize - served.length;
  return withheld > 0
    ? { answered, total, completeness: 'unknowable', stoppedAt: null }
    : { answered, total, completeness: 'complete', stoppedAt: null };
}

/** What the hook is doing, so the panel can stay quiet rather than guess. */
export type QuestionnaireProgressState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Read the progress for one assessment. `null` assessmentId issues no request
 * and reports `idle` — a fresh lead has no questionnaire to measure.
 *
 * BOTH FAILURES ARE THE SAME OUTCOME: `error` with no progress. The panel then
 * renders the rung with no line under it, which is exactly what it renders while
 * loading, so a flaky read degrades to a slightly plainer panel instead of an
 * error somebody has to dismiss on a screen they opened to read a journey.
 */
export function useQuestionnaireProgress(assessmentId: string | null): {
  state: QuestionnaireProgressState;
  progress: QuestionnaireProgress | null;
} {
  const [read, setRead] = useState<{
    state: QuestionnaireProgressState;
    progress: QuestionnaireProgress | null;
  }>({ state: 'idle', progress: null });

  useEffect(() => {
    if (!assessmentId) {
      setRead({ state: 'idle', progress: null });
      return;
    }
    let cancelled = false;
    setRead({ state: 'loading', progress: null });
    Promise.all([
      getQuestions(assessmentId),
      // ⚠️ R19 — the values are dropped HERE, at the boundary, and never reach
      // React state. See the module docblock.
      getAssessmentAnswers(assessmentId).then((read) => Object.keys(read.answers)),
    ])
      .then(([set, answerKeys]) => {
        if (cancelled) return;
        setRead({ state: 'ready', progress: deriveQuestionnaireProgress(set.questions, answerKeys) });
      })
      .catch(() => {
        if (!cancelled) setRead({ state: 'error', progress: null });
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  return read;
}
